import { useState, useEffect } from "react";
import { FilePdf, LinkSimple, Copy, X, DownloadSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SECTION_OPTIONS } from "./constants";

const SECTION_TO_EXPAND_KEYS = ["spend", "comparison", "expenses", "vendor", "revenue"];
// Sections that contain long tables and SHOULD be allowed to break across pages.
// All other sections should stay on a single page (page-break-inside: avoid).
const TABLE_SECTIONS = new Set(["expenses", "vendor", "revenue"]);

export function ExportShareModal({
  open, onClose, mode, shareMode, setShareMode, sectionPick, setSectionPick,
  businessId, businessName, lastDataDate, rootRef, generatedShareUrl, setGeneratedShareUrl, setExpanded,
}) {
  const [busy, setBusy] = useState(false);
  // PDF "Ready to download" popup state
  const [pdfReady, setPdfReady] = useState(null); // { url, filename, sizeBytes }
  // Default orientation = landscape
  const [orientation, setOrientation] = useState("landscape");

  // Cleanup blob URL on close
  useEffect(() => {
    if (!open && pdfReady) {
      setTimeout(() => URL.revokeObjectURL(pdfReady.url), 250);
      setPdfReady(null);
    }
  }, [open, pdfReady]);

  if (!open) return null;

  const toggleSection = (k) => setSectionPick((s) => ({ ...s, [k]: !s[k] }));
  const allOn = SECTION_OPTIONS.every((s) => sectionPick[s.key]);
  const setAll = (v) => setSectionPick(Object.fromEntries(SECTION_OPTIONS.map((s) => [s.key, v])));

  const handleGeneratePdf = async () => {
    if (!rootRef.current) return;
    setBusy(true);
    try {
      const needExpand = {};
      SECTION_TO_EXPAND_KEYS.forEach((k) => { if (sectionPick[k]) needExpand[k] = true; });
      setExpanded((s) => ({ ...s, ...needExpand }));
      await new Promise((r) => setTimeout(r, 700));
      if (document.fonts?.ready) {
        try { await document.fonts.ready; } catch {}
      }

      const hidden = [];
      rootRef.current.querySelectorAll("[data-section]").forEach((el) => {
        const key = el.getAttribute("data-section");
        if (!sectionPick[key]) {
          hidden.push([el, el.style.display]);
          el.style.display = "none";
        }
      });
      rootRef.current.querySelectorAll("[data-section-controls]").forEach((el) => {
        hidden.push([el, el.style.display]);
        el.style.display = "none";
      });

      // Apply page-break-inside: avoid ONLY to non-table sections so cards stay whole,
      // but long tables (expenses / vendor / revenue) can flow across pages.
      // Use BOTH inline CSS and a data-attribute so html2pdf's pagebreak.avoid
      // selector picks them up reliably (CSS-only avoid is sometimes ignored
      // when the section happens to span the natural page break).
      const sectioned = rootRef.current.querySelectorAll("[data-section]");
      sectioned.forEach((el) => {
        const key = el.getAttribute("data-section");
        if (!TABLE_SECTIONS.has(key)) {
          el.style.pageBreakInside = "avoid";
          el.style.breakInside = "avoid";
          el.setAttribute("data-pdf-keep", "1");
        }
      });

      // Each individual KPI card inside the kpis row should also avoid splitting,
      // so if the 3-card row doesn't fit a page, each card still stays intact.
      const kpiRow = rootRef.current.querySelector('[data-section="kpis"]');
      if (kpiRow) {
        kpiRow.querySelectorAll(":scope > *").forEach((card) => {
          card.style.pageBreakInside = "avoid";
          card.style.breakInside = "avoid";
          card.setAttribute("data-pdf-keep", "1");
        });
      }

      // ---- PDF capture prep ----
      // Many UI elements clip content for the live UI (overflow-hidden cards,
      // overflow-x-auto table wrappers, truncate on section titles). These cause
      // tables to lose their rightmost column and titles to be chopped in the PDF.
      // For the duration of capture we relax all clipping so html2canvas can see
      // the full content, then we restore everything.
      const styleSnaps = [];
      const snap = (el, props) => {
        const prev = {};
        props.forEach((p) => { prev[p] = el.style[p]; });
        styleSnaps.push([el, prev]);
      };

      rootRef.current.querySelectorAll(".overflow-hidden, .overflow-x-auto, .overflow-y-auto, .overflow-auto").forEach((el) => {
        snap(el, ["overflow", "overflowX", "overflowY"]);
        el.style.overflow = "visible";
        el.style.overflowX = "visible";
        el.style.overflowY = "visible";
      });
      // Untruncate section / card titles so things like "Budget vs Spent by Category"
      // and "Expenses by Vendor" render in full instead of getting ellipsised.
      rootRef.current.querySelectorAll(".truncate, h1, h2, h3, h4").forEach((el) => {
        snap(el, ["whiteSpace", "overflow", "textOverflow"]);
        el.style.whiteSpace = "normal";
        el.style.overflow = "visible";
        el.style.textOverflow = "clip";
      });

      // Nudge Recharts ResponsiveContainer to remeasure now that ancestors are no
      // longer clipped — gives the pie / bar chart their full container width.
      window.dispatchEvent(new Event("resize"));
      await new Promise((r) => setTimeout(r, 350));

      // Pre-compute header content
      const exportedAtStr = new Date().toLocaleString();
      const transactionDateStr = lastDataDate
        ? new Date(lastDataDate + "T00:00:00").toLocaleDateString()
        : null;

      try {
        const html2pdf = (await import("html2pdf.js")).default;
        const filename = `${(businessName || "project-summary").replace(/[^a-z0-9 _-]/gi, "_")}.pdf`;

        // Orientation-aware layout. Landscape has more horizontal space so we use
        // a wider capture viewport and slightly tighter top margin for the header.
        const isLandscape = orientation === "landscape";
        const layout = isLandscape
          ? { margin: [24, 12, 10, 12], windowWidth: 2000 }
          : { margin: [26, 12, 12, 12], windowWidth: 1700 };

        const worker = html2pdf()
          .set({
            margin: layout.margin, // [top, right, bottom, left] in mm
            filename,
            image: { type: "jpeg", quality: 0.95 },
            html2canvas: { scale: 2, useCORS: true, backgroundColor: "#F4F6F8", logging: false, scrollX: 0, scrollY: 0, windowWidth: layout.windowWidth },
            jsPDF: { unit: "mm", format: "a4", orientation, compress: true },
            pagebreak: { mode: ["css", "legacy"], avoid: "[data-pdf-keep]" },
          })
          .from(rootRef.current);

        // Build the PDF and then draw a header bar on every page.
        const pdf = await worker.toPdf().get("pdf");
        const totalPages = pdf.internal.getNumberOfPages();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        // Header bar height matches the top margin minus a small gap so content
        // starts cleanly below the divider line.
        const headerBottom = layout.margin[0] - 4;

        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);

          // Subtle background strip for the header
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, headerBottom, "F");

          // Brand "Strata" + small caps subtitle on the left
          pdf.setTextColor(15, 23, 42); // slate-900
          pdf.setFont("times", "bold");
          pdf.setFontSize(15);
          pdf.text("Strata", 12, 9);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(6);
          pdf.setTextColor(100, 116, 139); // slate-500
          pdf.text("BY FRIEDMAN GROUP", 12, 13);

          // Business name row — sits right below the brand block on every page
          if (businessName) {
            pdf.setTextColor(50, 125, 227); // brand blue
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(10);
            pdf.text(businessName.toUpperCase(), 12, 19);
          }

          // Right side: pastel red export date/time + transaction data last updated
          pdf.setTextColor(239, 108, 108); // soft pastel red
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(8);
          pdf.text(`Exported: ${exportedAtStr}`, pageWidth - 12, 9, { align: "right" });
          if (transactionDateStr) {
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(7.5);
            pdf.text(
              `Transaction Data Last Updated: ${transactionDateStr}`,
              pageWidth - 12,
              13,
              { align: "right" }
            );
          }

          // Divider line in brand blue
          pdf.setDrawColor(50, 125, 227);
          pdf.setLineWidth(0.4);
          pdf.line(12, headerBottom, pageWidth - 12, headerBottom);

          // Footer: "Page i of N" — bottom-right corner
          pdf.setTextColor(100, 116, 139); // slate-500
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 12, pageHeight - 6, { align: "right" });
        }

        const blob = pdf.output("blob");
        const url = URL.createObjectURL(blob);
        // Show download-ready popup instead of auto-saving
        setPdfReady({ url, filename, sizeBytes: blob.size });
        toast.success("PDF generated — click Download below");
      } finally {
        hidden.forEach(([el, prev]) => { el.style.display = prev || ""; });
        sectioned.forEach((el) => {
          el.style.pageBreakInside = "";
          el.style.breakInside = "";
          el.removeAttribute("data-pdf-keep");
        });
        // Restore the per-KPI-card avoid markers too
        const kpiRowEl = rootRef.current?.querySelector('[data-section="kpis"]');
        if (kpiRowEl) {
          kpiRowEl.querySelectorAll(":scope > *").forEach((card) => {
            card.style.pageBreakInside = "";
            card.style.breakInside = "";
            card.removeAttribute("data-pdf-keep");
          });
        }
        // Restore relaxed clip / truncate styles
        styleSnaps.forEach(([el, prev]) => {
          Object.entries(prev).forEach(([k, v]) => { el.style[k] = v || ""; });
        });
      }
    } catch (e) {
      console.error("PDF export error", e);
      toast.error(`PDF export failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const closeAll = () => {
    if (pdfReady) {
      URL.revokeObjectURL(pdfReady.url);
      setPdfReady(null);
    }
    onClose();
  };

  const handleCreateShare = async () => {
    setBusy(true);
    try {
      const sections = SECTION_OPTIONS.filter((s) => sectionPick[s.key]).map((s) => s.key);
      const { data } = await api.post(`/businesses/${businessId}/share`, { sections, mode: shareMode });
      const url = `${window.location.origin}/share/${data.token}`;
      setGeneratedShareUrl(url);
      toast.success("Share link created");
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  // PDF Ready popup — replaces the form once a blob is generated
  if (pdfReady) {
    const mb = (pdfReady.sizeBytes / 1024 / 1024).toFixed(2);
    return (
      <div className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center px-4" data-testid="pdf-ready-modal">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <CheckCircle size={20} weight="fill" className="text-emerald-500" />
              <h2 className="font-display text-lg font-semibold text-slate-900">Your PDF is ready</h2>
            </div>
            <button onClick={closeAll} className="text-slate-400 hover:text-slate-700" data-testid="pdf-ready-close">
              <X size={18} />
            </button>
          </div>
          <div className="px-6 py-5">
            <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
              <div className="w-10 h-10 rounded-md bg-[#D92D20]/10 flex items-center justify-center shrink-0">
                <FilePdf size={22} weight="bold" className="text-[#D92D20]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 text-sm truncate" data-testid="pdf-ready-filename">{pdfReady.filename}</div>
                <div className="text-xs text-slate-500">{mb} MB · PDF document</div>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-600">
              Click the button below to download your report. The link stays valid until you close this dialog.
            </p>
          </div>
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
            <Button onClick={closeAll} variant="outline" className="border-slate-300" data-testid="pdf-ready-cancel">Close</Button>
            <a
              href={pdfReady.url}
              download={pdfReady.filename}
              data-testid="pdf-ready-download"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-[#327de3] hover:bg-[#1f64c2] text-white font-medium text-sm transition-colors"
            >
              <DownloadSimple size={16} weight="bold" /> Download PDF
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center px-4" data-testid="export-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {mode === "pdf" ? <FilePdf size={20} weight="bold" className="text-[#D92D20]" /> : <LinkSimple size={20} weight="bold" className="text-[#327de3]" />}
            <h2 className="font-display text-lg font-semibold text-slate-900">
              {mode === "pdf" ? "Export to PDF" : "Share read-only link"}
            </h2>
          </div>
          <button onClick={closeAll} className="text-slate-400 hover:text-slate-700" data-testid="modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500">Include sections</h3>
              <button
                onClick={() => setAll(!allOn)}
                className="text-xs font-medium text-[#327de3] hover:text-[#1f64c2]"
                data-testid="toggle-all-sections"
              >
                {allOn ? "Deselect all" : "Select all"}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {SECTION_OPTIONS.map((opt) => (
                <label key={opt.key} className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid={`section-${opt.key}`}>
                  <input
                    type="checkbox"
                    checked={!!sectionPick[opt.key]}
                    onChange={() => toggleSection(opt.key)}
                    className="h-4 w-4 accent-[#327de3]"
                  />
                  <span className="text-sm text-slate-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {mode === "pdf" && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Page orientation</h3>
              <div className="grid grid-cols-2 gap-2" data-testid="orientation-toggle">
                {[
                  { value: "landscape", label: "Landscape", hint: "Wide layout · best for tables & charts" },
                  { value: "portrait", label: "Portrait", hint: "Tall layout · best for one-page summaries" },
                ].map((opt) => {
                  const active = orientation === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setOrientation(opt.value)}
                      className={`text-left px-3 py-2.5 rounded-md border transition-all ${
                        active
                          ? "border-[#327de3] bg-[#327de3]/5 ring-1 ring-[#327de3]/30"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid={`orientation-${opt.value}`}
                    >
                      <div className={`text-sm font-semibold ${active ? "text-[#1f64c2]" : "text-slate-800"}`}>{opt.label}</div>
                      <div className="text-[11px] text-slate-500">{opt.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {mode === "share" && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Link type</h3>
              <div className="space-y-2">
                <label className="flex items-start gap-3 px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input type="radio" name="shareMode" value="live" checked={shareMode === "live"} onChange={() => setShareMode("live")} className="mt-0.5 accent-[#327de3]" data-testid="share-mode-live" />
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Live data</div>
                    <div className="text-xs text-slate-500">Viewers will see the latest cached data on each visit.</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input type="radio" name="shareMode" value="snapshot" checked={shareMode === "snapshot"} onChange={() => setShareMode("snapshot")} className="mt-0.5 accent-[#327de3]" data-testid="share-mode-snapshot" />
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Snapshot</div>
                    <div className="text-xs text-slate-500">Freeze the current data at this moment. The link will never auto-update.</div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {mode === "share" && generatedShareUrl && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Share URL</div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={generatedShareUrl}
                  className="flex-1 text-sm bg-white border border-slate-200 rounded-md px-3 py-2 font-mono"
                  data-testid="share-url-input"
                />
                <Button onClick={() => copyLink(generatedShareUrl)} variant="outline" className="border-slate-300 h-10" data-testid="copy-share-url">
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button onClick={closeAll} variant="outline" className="border-slate-300">Cancel</Button>
          {mode === "pdf" ? (
            <Button onClick={handleGeneratePdf} disabled={busy} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="modal-export-pdf">
              {busy ? "Generating…" : "Generate PDF"}
            </Button>
          ) : (
            <Button onClick={handleCreateShare} disabled={busy} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="modal-generate-share">
              {busy ? "Creating…" : generatedShareUrl ? "Regenerate link" : "Generate link"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
